import React, { useState, useEffect } from 'react';

// --- ICONOS SVG LOCALES ---
const IconCalendar = () => (
    <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-900 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const IconChevronDown = () => (
    <svg className="w-3.5 h-3.5 text-slate-500 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const DateRangePicker = ({ fechaDesde, setFechaDesde, fechaHasta, setFechaHasta, onSubmit }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activePreset, setActivePreset] = useState('');

    // Formateador interno para strings YYYY-MM-DD requeridos por inputs de tipo date
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper de cálculo de presets
    const aplicarPreset = (tipo, etiqueta) => {
        const hoy = new Date();
        let desde = new Date();

        switch (tipo) {
            case '15_DIAS':
                desde.setDate(hoy.getDate() - 15);
                break;
            case '1_MES':
                desde.setMonth(hoy.getMonth() - 1);
                break;
            case '3_MESES':
                desde.setMonth(hoy.getMonth() - 3);
                break;
            case 'ESTE_ANO':
                desde = new Date(hoy.getFullYear(), 0, 1); // 1 de Enero del año en curso
                break;
            case '1_ANO':
                desde.setFullYear(hoy.getFullYear() - 1);
                break;
            default:
                return;
        }

        setFechaDesde(formatDate(desde));
        setFechaHasta(formatDate(hoy));
        setActivePreset(etiqueta);
        setDropdownOpen(false);
    };

    // Cierra el menú flotante si se hace clic afuera
    useEffect(() => {
        if (!dropdownOpen) return;
        const closeMenu = () => setDropdownOpen(false);
        document.addEventListener('click', closeMenu);
        return () => document.removeEventListener('click', closeMenu);
    }, [dropdownOpen]);

    // Resetea el preset activo si el usuario cambia manualmente las fechas en los inputs
    const handleManualChangeDesde = (val) => {
        setFechaDesde(val);
        setActivePreset('');
    };

    const handleManualChangeHasta = (val) => {
        setFechaHasta(val);
        setActivePreset('');
    };

    return (
        <div className="flex items-center gap-3">
            {/* CONTENEDOR PRINCIPAL DEL SELECTOR DE RANGOS */}
            <form 
                onSubmit={onSubmit} 
                className="flex items-center gap-3 bg-white p-1.5 pl-3 border border-slate-200/80 rounded-2xl shadow-sm hover:border-slate-300 transition-all duration-200"
            >
                {/* CAMPO: FECHA DESDE */}
                <div className="flex items-center gap-2 group cursor-pointer">
                    <IconCalendar />
                    <div className="flex flex-col text-left">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider leading-none">Carga Desde</span>
                        <input 
                            type="date" 
                            value={fechaDesde} 
                            onChange={(e) => handleManualChangeDesde(e.target.value)} 
                            className="bg-transparent border-none p-0.5 text-xs font-bold font-mono text-slate-700 outline-none cursor-pointer focus:ring-0 mt-0.5" 
                            required 
                        />
                    </div>
                </div>
                
                {/* DIVISOR DE CAMPOS */}
                <div className="h-6 w-px bg-slate-200"></div>

                {/* CAMPO: FECHA HASTA */}
                <div className="flex items-center gap-2 pr-1">
                    <div className="flex flex-col text-left">
                        <span className="text-[8px] font-black text-slate-400 uppercase tracking-wider leading-none">Hasta</span>
                        <input 
                            type="date" 
                            value={fechaHasta} 
                            onChange={(e) => handleManualChangeHasta(e.target.value)} 
                            className="bg-transparent border-none p-0.5 text-xs font-bold font-mono text-slate-700 outline-none cursor-pointer focus:ring-0 mt-0.5" 
                            required 
                        />
                    </div>
                </div>

                {/* BOTÓN SUBMIT DE CARGA */}
                <button 
                    type="submit" 
                    className="py-2 px-4 bg-[#01032d] hover:bg-[#0c0e3e] text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200 shadow-md shadow-[#01032d]/10 active:scale-[0.98]"
                >
                    Filtrar
                </button>
            </form>

            {/* CONTENEDOR DE ATADOS RÁPIDOS (PRESETS DROPDOWN) */}
            <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200/80 rounded-2xl text-xs font-bold uppercase tracking-wider transition-all shadow-sm active:scale-[0.98]"
                >
                    <span>{activePreset || 'Atajos de Fecha'}</span>
                    <span className={`transform transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                        <IconChevronDown />
                    </span>
                </button>

                {/* DROPDOWN FLOTANTE */}
                {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200/85 rounded-2xl shadow-xl z-50 p-1.5 animate-scale-up">
                        <div className="px-3 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-1">
                            Selección Rápida
                        </div>
                        <button
                            type="button"
                            onClick={() => aplicarPreset('15_DIAS', 'Últimos 15 Días')}
                            className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex items-center justify-between"
                        >
                            <span>Últimos 15 Días</span>
                            <span className="text-[10px] font-mono text-slate-400">15d</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => aplicarPreset('1_MES', 'Último Mes')}
                            className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex items-center justify-between"
                        >
                            <span>Último Mes</span>
                            <span className="text-[10px] font-mono text-slate-400">30d</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => aplicarPreset('3_MESES', 'Últimos 3 Meses')}
                            className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex items-center justify-between"
                        >
                            <span>Últimos 3 Meses</span>
                            <span className="text-[10px] font-mono text-slate-400">90d</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => aplicarPreset('ESTE_ANO', 'Este Año')}
                            className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex items-center justify-between"
                        >
                            <span>Este Año</span>
                            <span className="text-[10px] font-mono text-slate-400">YTD</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => aplicarPreset('1_ANO', 'Último Año')}
                            className="w-full text-left px-3 py-2 text-xs font-bold text-slate-600 hover:text-[#01032d] hover:bg-slate-50 rounded-xl transition-all flex items-center justify-between"
                        >
                            <span>Último Año</span>
                            <span className="text-[10px] font-mono text-slate-400">365d</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DateRangePicker;